import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
    useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    fetchCredentialingDevices,
    fetchInsertedCredentialFlow,
    fetchNormalCredentialFlow,
} from '../../../utils/credentialing';
import { emptyObject } from '../../../utils/generic';
import { issuanceTypeCardId } from '../../../constants';
import IssuanceTypeWithData from './IssuanceTypeWithData';
import { AppDispatchContext } from '../../../context/AppContext';
import CardLayout from '../../shared/CardLayout';
import {
    EnrollingDispatchContext,
    EnrollingStateContext,
} from '../../../context/EnrollingContext';
import { useDidMount } from '../../../utils/hooks';

export const parseFieldSet = (json, fieldset, t) => {
    let newFieldSet = {};
    // handle first time render
    if (fieldset === null) {
        Object.keys(json).forEach((key) => {
            newFieldSet[key] = {
                selected: false,
                display: true,
                ...json[key],
            };
        });
        // UCMS returns a separate issuance type per platform variant (e.g.
        // axiadid_ios and axiadid_android) so the Portal knows which
        // platforms are available. However, platform variants that share the
        // same display label should appear as a single radio button — the
        // platform distinction is handled later in the enrollment flow.
        // We hide duplicates by resolved i18n label so this works
        // generically for any device-type family (axiadid, msauth, etc.)
        // without hardcoding type names.
        if (t) {
            const seenLabels = {};
            Object.keys(newFieldSet).forEach((key) => {
                const label = t(`issuanceTypeCardLabels$issuanceTypes$${key}`);
                if (seenLabels[label]) {
                    newFieldSet[key].display = false;
                } else {
                    seenLabels[label] = true;
                }
            });
        }
    } else {
        newFieldSet = { ...fieldset };
    }

    const firstOptionKey = Object.keys(json).find(
        (key) => newFieldSet[key] && newFieldSet[key].display,
    ) || Object.keys(json)[0];
    if (firstOptionKey) {
        newFieldSet[firstOptionKey].selected = true;
        Object.keys(newFieldSet).forEach((key) => {
            if (key !== firstOptionKey) {
                newFieldSet[key].selected = false;
            }
        });
    }
    return newFieldSet;
};

const IssuanceTypeCard = ({ mgmt, path }) => {
    const dispatch = useContext(AppDispatchContext);
    const enrollingDispatch = useContext(EnrollingDispatchContext);
    const { reader, username } = useContext(EnrollingStateContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [fieldset, setFieldset] = useState(null);
    const [allowedDevices, setAllowedDevices] = useState({});
    const [showNext, setShowNext] = useState(false);
    const [nextClicked, setNextClicked] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [assuranceAckType, setAssuranceAckType] = useState('insertedDevice');

    const lastUsername = useRef(null);

    const setDevicesCallback = useCallback(
        (json) => {
            setAllowedDevices(json);
            const newFieldSet = parseFieldSet(json, fieldset, t);
            setFieldset(newFieldSet);
            setIsLoading(false);
        },
        [fieldset, t],
    );

    const handleRadioClick = (proxy) => {
        const newFieldset = { ...fieldset };
        Object.keys(newFieldset).forEach((key) => {
            newFieldset[key].selected = false;
            if (key === proxy.target.value) {
                newFieldset[key].selected = true;
            }
        });
        setFieldset(newFieldset);
        setAssuranceAckType(proxy.target.value);
    };

    const back = async () => {
        navigate(path);
    };

    const next = async () => {
        setNextClicked(true);
        const setCredentialFlow = (credentialFlow) =>
            enrollingDispatch({
                type: 'SET_CREDENTIAL_FLOW',
                credentialFlow,
            });

        Object.keys(fieldset).forEach(async (key) => {
            const obj = fieldset[key];

            if (obj.selected) {
                // Todo: refactor when more rideo button have "dropdown" buttonType besides insertedDevice
                if (obj.buttonType === 'dropdown') {
                    if (mgmt && username) {
                        await fetchInsertedCredentialFlow(
                            {
                                setCredentialFlow,
                                setWorkflow: mgmt.setWorkflow,
                                dispatch,
                            },
                            reader.type,
                            reader.serial,
                            {
                                group: reader.groups && reader.groups[0],
                                subType: reader.subType,
                                prestate: reader.prestate,
                            },
                            username,
                        );
                        mgmt.next();
                    }
                } else {
                    enrollingDispatch({
                        type: 'SET_SELECTED_READER',
                        reader: {groups: obj.group && [obj.group]},
                    });
                    if (mgmt && username) {
                        await fetchNormalCredentialFlow(
                            {
                                setCredentialFlow,
                                setWorkflow: mgmt.setWorkflow,
                                dispatch,
                            },
                            username,
                            key,
                        );
                        if (key.toLowerCase().includes('axiadid')) {
                            const allKeys = Object.keys(fieldset).map(k => k.toLowerCase());
                            const availablePlatforms = {
                                ios: allKeys.some(k => k.includes('axiadid_ios')),
                                android: allKeys.some(k => k.includes('axiadid_android')),
                            };
                            enrollingDispatch({
                                type: 'SET_SPONSOR_DEVICE_TYPE',
                                sponsorDeviceType: obj.sponsorDeviceType || null,
                            });
                            dispatch({
                                type: 'SHOW_MODAL',
                                modal: {
                                    type: 'PIV_SETUP_GUIDANCE',
                                    closeAction: () => mgmt.next(),
                                    sponsorDeviceType: obj.sponsorDeviceType || null,
                                    availablePlatforms,
                                },
                            });
                        } else {
                            mgmt.next();
                        }
                    }
                }
            }
        });
    };

    useDidMount(() => {
        if (mgmt !== undefined && mgmt.resetMgmtState !== undefined) {
            mgmt.resetMgmtState();
        }
        enrollingDispatch({
            type: 'SET_SELECTED_READER',
            reader: null,
        });
    });

    useEffect(() => {
        if (username && lastUsername.current !== username) {
            lastUsername.current = username;
            fetchCredentialingDevices(setDevicesCallback, dispatch, username);
        }
    }, [username, setDevicesCallback, dispatch]);

    useEffect(() => {
        const isRadioButtonSelected = () => {
            let res = false;
            Object.keys(fieldset).forEach((key) => {
                if (!res) {
                    const obj = fieldset[key];
                    const dropDownSelected =
                        obj.buttonType === 'dropdown' &&
                        obj.selected &&
                        !emptyObject(reader);
                    const normalSelected =
                        obj.buttonType === 'normal' && obj.selected;
                    if (normalSelected || dropDownSelected) {
                        res = true;
                    }
                }
            });
            return res;
        };
        const enableNext = () => {
            if (nextClicked === true || fieldset === null) {
                return false;
            }

            return isRadioButtonSelected();
        };
        setShowNext(enableNext());
    }, [nextClicked, reader, fieldset]);

    return (
        <CardLayout
            title={t('cardTitles$issueIdentity')}
            showNext={showNext}
            showBack
            mgmt={{
                ...mgmt,
                next,
                back,
            }}
            testId={issuanceTypeCardId}
            assuranceAckType={assuranceAckType}
        >
            {fieldset !== null && (
                <IssuanceTypeWithData
                    fieldset={fieldset}
                    handleRadioClick={handleRadioClick}
                    isLoading={isLoading}
                    allowedDevices={allowedDevices}
                />
            )}
        </CardLayout>
    );
};

IssuanceTypeCard.propTypes = {
    mgmt: PropTypes.object.isRequired,
};

export default IssuanceTypeCard;
